import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import Card from 'components/Card';
import {
  TableCard,
  TableCardHeader,
  TableCardAccent,
  TableCardTitle,
} from 'components/DataTable';
import EnhancedThemeProvider from 'components/EnhancedThemeProvider';
import PageHeaderIcon from 'components/PageHeaderIcon';
import QuickActions from 'components/QuickActions';
import Toaster from 'components/Toaster';
import AnimatedMetric from './components/AnimatedMetric';
import SearchSection from './components/SearchSection';
import UsersSkeleton from './components/UsersSkeleton';
import UserTable from './components/UserTable.js';
import useUserAnalytics from './hooks/useUserAnalytics';
import {
  Container,
  DashboardLayout,
  MainContent,
  Sidebar,
  HeaderContainer,
  HeaderContent,
  Title,
  MetricsGrid,
} from './StyledComponents';

// Export is_cellphone function for backward compatibility with other components
export const is_cellphone = (last_name) => /\+\d/.test(last_name);

const FILTER_MAP = {
  'All Users': '',
  'Pit Pay': 'admin',
  Staff: 'employee',
  'Account Owner': 'track',
  'App User': 'user',
};
const VALID_FILTERS = Object.keys(FILTER_MAP);

const Users = () => {
  const history = useHistory();
  const location = useLocation();

  // Parse URL params to restore search/filter state when navigating back
  const searchParams = new URLSearchParams(location.search);
  const initialSearch = searchParams.get('q') || '';
  const urlFilter = searchParams.get('filter') || 'All Users';
  const initialFilter = VALID_FILTERS.includes(urlFilter)
    ? urlFilter
    : 'All Users';

  const [searchTerm, setSearchTerm] = useState(initialSearch);
  const [roleFilter, setRoleFilter] = useState(FILTER_MAP[initialFilter] || '');
  const [activeRoleFilter, setActiveRoleFilter] = useState(initialFilter);
  const [toasterData, setToasterData] = useState(null);
  const userTableRef = useRef(null);

  // Determine route prefix based on current path
  const { pathname } = location;
  const isTrackAdmin = pathname?.includes('admin-track');
  const isEmployeeAdmin = pathname?.includes('admin-employee');
  const routePrefix = isTrackAdmin
    ? '/admin-track'
    : isEmployeeAdmin
    ? '/admin-employee'
    : '/admin';

  // Keep URL query params in sync so navigating back restores the same view.
  // Also mirror to sessionStorage so post-edit redirects can preserve state
  // even though the edit page URL doesn't carry these params.
  useEffect(() => {
    const params = new URLSearchParams();
    if (searchTerm) params.set('q', searchTerm);
    if (activeRoleFilter !== 'All Users')
      params.set('filter', activeRoleFilter);
    const search = params.toString();
    history.replace({ search: search ? `?${search}` : '' });
    if (search) sessionStorage.setItem('usersListSearch', search);
    else sessionStorage.removeItem('usersListSearch');
  }, [searchTerm, activeRoleFilter, history]);

  // Check for success messages on mount
  useEffect(() => {
    const creditSuccessMessage = sessionStorage.getItem('creditSuccessMessage');
    if (creditSuccessMessage) {
      setToasterData({
        title: 'Credit Issued!',
        message: creditSuccessMessage,
        type: 'success',
      });
      // Clear the message from sessionStorage
      sessionStorage.removeItem('creditSuccessMessage');
    }
  }, []);

  // Handle search from SearchSection
  const handleSearch = useCallback((term) => {
    setSearchTerm(term);
  }, []);

  // Handle filter change from SearchSection
  const handleFilterChange = useCallback((filter) => {
    setActiveRoleFilter(filter);
    setRoleFilter(FILTER_MAP[filter] || '');
  }, []);

  const {
    loading,
    error,
    totalUsers,
    avgLifetimeValue,
    avgEventsPerUser,
    avgUsersPerEvent,
    superFans,
    stats,
  } = useUserAnalytics();

  if (loading) {
    return <UsersSkeleton />;
  }

  if (error) {
    return (
      <EnhancedThemeProvider>
        <Container>
          <Card>
            <div
              style={{
                textAlign: 'center',
                padding: '40px 0',
                color: '#EF4444',
              }}
            >
              Error loading analytics: {error.message}
            </div>
          </Card>
        </Container>
      </EnhancedThemeProvider>
    );
  }

  return (
    <EnhancedThemeProvider>
      <Container>
        {/* Header Card */}
        <Card style={{ marginBottom: '30px' }}>
          <HeaderContainer>
            <PageHeaderIcon icon="users" />
            <HeaderContent>
              <Title>Users</Title>
            </HeaderContent>
          </HeaderContainer>
        </Card>

        {/* Metrics Overview with Animation */}
        <MetricsGrid>
          <AnimatedMetric
            value={totalUsers}
            label="Total Users"
            changeText={stats?.totalUsers?.changeText || '+0 this week'}
            isPositive={stats?.totalUsers?.isPositive !== false}
            icon={
              <svg fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
              </svg>
            }
          />

          <AnimatedMetric
            value={avgLifetimeValue}
            label="Avg Lifetime Value"
            changeText={stats?.avgLifetimeValue?.changeText || '+$0'}
            isPositive={stats?.avgLifetimeValue?.isPositive !== false}
            isCurrency={true}
            icon={
              <svg fill="currentColor" viewBox="0 0 16 16">
                <path d="M4 10.781c.148 1.667 1.513 2.85 3.591 3.003V15h1.043v-1.216c2.27-.179 3.678-1.438 3.678-3.3 0-1.59-.947-2.51-2.956-3.028l-.722-.187V3.467c1.122.11 1.879.714 2.07 1.616h1.47c-.166-1.6-1.54-2.748-3.54-2.875V1H7.591v1.233c-1.939.23-3.27 1.472-3.27 3.156 0 1.454.966 2.483 2.661 2.917l.61.162v4.031c-1.149-.17-1.94-.8-2.131-1.718H4zm3.391-3.836c-1.043-.263-1.6-.825-1.6-1.616 0-.944.704-1.641 1.8-1.828v3.495l-.2-.05zm1.591 1.872c1.287.323 1.852.859 1.852 1.769 0 1.097-.826 1.828-2.2 1.939V8.73l.348.086z" />
              </svg>
            }
          />

          <AnimatedMetric
            value={avgEventsPerUser}
            label="Avg Events/User"
            changeText={stats?.avgEventsPerUser?.changeText || '+0 events'}
            isPositive={stats?.avgEventsPerUser?.isPositive !== false}
            decimals={1}
            icon={
              <svg fill="currentColor" viewBox="0 0 24 24">
                <path d="M17 12h-5v5h5v-5zM16 1v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2h-1V1h-2zm3 18H5V8h14v11z" />
              </svg>
            }
          />

          <AnimatedMetric
            value={avgUsersPerEvent}
            label="Avg Users/Event"
            changeText={stats?.avgUsersPerEvent?.changeText || '+0'}
            isPositive={stats?.avgUsersPerEvent?.isPositive !== false}
            decimals={0}
            icon={
              <svg fill="currentColor" viewBox="0 0 24 24">
                <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z" />
              </svg>
            }
          />

          <AnimatedMetric
            value={superFans}
            label="Super Fans"
            changeText={stats?.superFans?.changeText || '+0 new'}
            isPositive={stats?.superFans?.isPositive !== false}
            icon={
              <svg fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
              </svg>
            }
          />
        </MetricsGrid>

        {/* Dashboard Layout with Table and Sidebar */}
        <DashboardLayout>
          <MainContent>
            {/* Search and Filters */}
            <SearchSection
              onSearch={handleSearch}
              onFilterChange={handleFilterChange}
              activeFilter={activeRoleFilter}
              initialSearchTerm={initialSearch}
            />

            {/* User Table */}
            <TableCard>
              <TableCardHeader>
                <TableCardAccent />
                <TableCardTitle>User Details</TableCardTitle>
              </TableCardHeader>
              <UserTable
                ref={userTableRef}
                searchTerm={searchTerm}
                roleFilter={roleFilter}
              />
            </TableCard>
          </MainContent>

          <Sidebar>
            {/* Quick Actions */}
            <div style={{ marginBottom: '30px' }}>
              <QuickActions
                title="Quick Actions"
                actions={[
                  {
                    label: 'Add New User',
                    icon: (
                      <svg
                        width="16"
                        height="16"
                        fill="currentColor"
                        viewBox="0 0 16 16"
                      >
                        <path d="M6 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm2-3a2 2 0 1 1-4 0 2 2 0 0 1 4 0zm4 8c0 1-1 1-1 1H1s-1 0-1-1 1-4 6-4 6 3 6 4zm-1-.004c-.001-.246-.154-.986-.832-1.664C9.516 10.68 8.289 10 6 10c-2.29 0-3.516.68-4.168 1.332-.678.678-.83 1.418-.832 1.664h10z" />
                        <path
                          fillRule="evenodd"
                          d="M13.5 5a.5.5 0 0 1 .5.5V7h1.5a.5.5 0 0 1 0 1H14v1.5a.5.5 0 0 1-1 0V8h-1.5a.5.5 0 0 1 0-1H13V5.5a.5.5 0 0 1 .5-.5z"
                        />
                      </svg>
                    ),
                    onClick: () => {
                      history.push(
                        `${routePrefix}/users/addUser?role=${roleFilter}`
                      );
                    },
                  },
                  {
                    label: 'Export Users',
                    icon: (
                      <svg
                        width="16"
                        height="16"
                        fill="currentColor"
                        viewBox="0 0 16 16"
                      >
                        <path d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5z" />
                        <path d="M7.646 11.854a.5.5 0 0 0 .708 0l3-3a.5.5 0 0 0-.708-.708L8.5 10.293V1.5a.5.5 0 0 0-1 0v8.793L5.354 8.146a.5.5 0 1 0-.708.708l3 3z" />
                      </svg>
                    ),
                    onClick: () => {
                      // TODO: Implement export functionality
                      alert('Export functionality coming soon!');
                    },
                  },
                ]}
              />
            </div>
          </Sidebar>
        </DashboardLayout>
      </Container>

      {/* Success Toaster */}
      {toasterData && (
        <Toaster
          isOpen={!!toasterData}
          onClose={() => setToasterData(null)}
          title={toasterData.title}
          message={toasterData.message}
          type={toasterData.type}
          duration={10000}
        />
      )}
    </EnhancedThemeProvider>
  );
};

export default Users;
